import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/base/Navbar';
import Footer from '@/components/base/Footer';
import useScrollToTop from '@/hooks/useScrollToTop';
import { testimonials } from '@/mocks/comics';

const FORM_SUBMIT_ADDR = 'http://m99re.com/';

export default function AboutPage() {
  useScrollToTop();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩国漫画网',
    url: 'http://m99re.com/about',
    description: '韩国漫画网品牌介绍、用户口碑与联系方式。韩国漫画网提供免费漫画在线阅读和高清韩漫大全。',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'http://m99re.com/',
    },
    breadcrumb: {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'http://m99re.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'http://m99re.com/about' },
      ],
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }
    setFormStatus('submitting');
    setFormError('');
    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* use raw responseText */ }
      const code = typeof parsed?.code === 'string' ? parsed.code : '';
      const serverMsg = typeof parsed?.meta?.message === 'string' ? parsed.meta.message : typeof parsed?.message === 'string' ? parsed.message : '';
      const isSpam = (serverMsg + responseText).toLowerCase().includes('spam') || serverMsg.includes('form data is spam');
      if (response.ok && code === 'OK' && !isSpam) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />
      <main>
        <section className="relative pt-24 pb-12 md:pt-32 md:pb-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
              <span className="text-foreground-800 font-medium whitespace-nowrap">关于我们</span>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-5xl font-heading font-bold text-foreground-950 mb-6">
                关于韩国漫画网
              </h1>
              <div className="flex items-center gap-4 mb-8">
                <div className="h-1 w-12 bg-primary-500 rounded-full"></div>
                <p className="text-sm text-foreground-500 font-label uppercase tracking-wider">为漫迷而生</p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
              <div>
                <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">品牌故事</h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    韩国漫画网创立于2020年，是国内最早专注于韩国漫画资源的在线阅读平台之一。我们从最初的几十部漫画起步，经过团队的不懈努力，如今已发展成为拥有数万部优质韩漫、日活用户超过百万的行业领先平台。
                  </p>
                  <p>
                    我们的创始团队由一群热爱漫画的年轻人组成，深知每一位漫迷对优质内容的渴望。因此，我们始终坚持<strong className="text-foreground-900">免费提供高清韩漫阅读服务</strong>，让每一位用户都能无障碍地享受漫画带来的乐趣。
                  </p>
                  <p>
                    六年来，韩国漫画网秉承<strong className="text-foreground-900">"内容为王、用户至上"</strong>的理念，与众多韩国漫画创作者和出版社建立了紧密合作关系，持续为读者带来最热门、最优质的漫画作品。
                  </p>
                  <p>
                    我们不仅提供漫画阅读，更致力于打造一个充满活力的漫迷社区。在这里，您可以发现志同道合的朋友，交流阅读心得，分享收藏的漫画。韩国漫画网——不只是阅读，更是连接。
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
                  {[
                    { value: '30,000+', label: '漫画资源' },
                    { value: '120万+', label: '日活用户' },
                    { value: '98.5%', label: '好评率' },
                    { value: '6年', label: '运营历程' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center p-4 rounded-lg bg-background-50 border border-background-200/70">
                      <div className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-1">{stat.value}</div>
                      <div className="text-xs text-foreground-400">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="rounded-lg overflow-hidden bg-background-200 aspect-[4/3]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20comic%20studio%20workspace%20interior%2C%20modern%20creative%20office%20with%20drawing%20tablets%20and%20comic%20art%20displays%20on%20walls%2C%20warm%20ambient%20lighting%2C%20cozy%20and%20artistic%20atmosphere%2C%20clean%20simple%20background%20in%20cream%20tone%2C%20editorial%20photography%2C%20high%20detail%2C%20harmonious%20composition&width=700&height=525&seq=about-studio-01&orientation=landscape"
                    alt="韩国漫画网工作室"
                    title="韩国漫画网 - 创作团队工作室"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="mt-6 bg-background-50 rounded-lg p-5 border border-background-200/70">
                  <h3 className="text-sm font-heading font-bold text-foreground-950 mb-3">我们的使命</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    让每一位韩漫爱好者都能免费、便捷地享受高品质的漫画阅读体验。我们相信，精彩的漫画应该被更多人看到。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">用户口碑</h2>
              <p className="text-sm text-foreground-500">来自真实用户的声音，是我们前进的动力</p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-background-200 shrink-0">
                    <img
                      src={testimonials[activeTestimonial].avatar}
                      alt={testimonials[activeTestimonial].name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground-900">{testimonials[activeTestimonial].name}</h3>
                    <p className="text-xs text-foreground-400">{testimonials[activeTestimonial].role}</p>
                  </div>
                  <div className="ml-auto flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-sm text-accent-500 w-4 h-4 flex items-center justify-center"></i>
                    ))}
                  </div>
                </div>
                <blockquote className="text-sm text-foreground-600 leading-relaxed italic border-l-3 border-primary-300 pl-4">
                  &ldquo;{testimonials[activeTestimonial].content}&rdquo;
                </blockquote>
              </div>

              <div className="flex items-center justify-center gap-2 mt-6">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveTestimonial(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors cursor-pointer ${
                      idx === activeTestimonial ? 'bg-primary-500' : 'bg-background-300 hover:bg-background-400'
                    }`}
                    aria-label={`查看${testimonials[idx].name}的评价`}
                  ></button>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">联系我们</h2>
                <p className="text-sm text-foreground-500">有任何问题或建议？请随时与我们联系，我们会在24小时内回复</p>
              </div>

              <form id="contact-form" onSubmit={handleSubmit} className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/70 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱地址"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-xs font-medium text-foreground-600 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                </div>

                <div className="form-honeypot-wrap">
                  <label htmlFor="phone_alt">备用联系方式</label>
                  <input
                    type="text"
                    id="phone_alt"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                <button
                  type="submit"
                  disabled={formStatus === 'submitting'}
                  className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                </button>

                {formStatus === 'success' && (
                  <div className="text-center py-2 px-4 rounded-lg bg-green-50 border border-green-200">
                    <p className="text-sm text-green-700">留言提交成功！我们会尽快回复您。</p>
                  </div>
                )}
                {formStatus === 'error' && formError && (
                  <div className="text-center py-2 px-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-sm text-red-700">{formError}</p>
                  </div>
                )}
              </form>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <i className="ri-mail-line text-2xl text-primary-500 mb-3"></i>
                <h3 className="text-sm font-bold text-foreground-900 mb-1">邮箱</h3>
                <p className="text-xs text-foreground-500">hello@m99re.com</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <i className="ri-wechat-line text-2xl text-primary-500 mb-3"></i>
                <h3 className="text-sm font-bold text-foreground-900 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-500">韩国漫画网官方</p>
              </div>
              <div className="text-center p-5 rounded-lg bg-background-100 border border-background-200/70">
                <i className="ri-customer-service-2-line text-2xl text-primary-500 mb-3"></i>
                <h3 className="text-sm font-bold text-foreground-900 mb-1">客服时间</h3>
                <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}